import { normalizeSize } from "../utils";

// -------------------------------
// DB-driven tab mapping
// -------------------------------
export function getComponentTab(component, spec, allTables) {
  const tableName = spec ? `structural_families_${spec}` : null;

  if (!tableName) {
    console.warn(`[getComponentTab] missing Spec`);
    return null;
  }

  const table = allTables?.[tableName];
  if (!Array.isArray(table) || table.length === 0) {
    console.warn(`[getComponentTab] table not found: ${tableName}`);
    return null;
  }

  const row = table.find((r) => r?.Component === component);
  if (!row) {
    console.warn(
      `[getComponentTab] Component "${component}" not found in ${tableName}`,
    );
    return null;
  }

  const tabName = row?.Parts_Lib_Tab;
  if (!tabName) {
    console.warn(
      `[getComponentTab] Parts_Lib_Tab empty for component "${component}"`,
    );
    return null;
  }

  return tabName;
}

// -------------------------------
// Size options
// -------------------------------
export function getSizesOptions(selectedParts, spec, component, allTables) {
  if (!spec || !component || !Array.isArray(selectedParts)) return [];

  const filtered = selectedParts.filter(
    (cur) => cur?.Spec === spec && cur?.Components === component,
  );

  const res = [];
  for (const cur of filtered) {
    try {
      const range = cur?.Size;
      const sizesInRange = getSizesInRange(range, component, spec, allTables);
      res.push(...sizesInRange.map((size) => ({ value: size, range })));
    } catch (e) {
      console.error(e);
    }
  }

  return res;
}

function getSizesInRange(range, component, spec, allTables) {
  const tableName = spec ? `structural_ranges_${spec}` : null;
  if (!tableName) {
    // console.warn(`[getSizesInRange] missing Spec`);
    return [];
  }

  const table = allTables?.[tableName];
  if (!Array.isArray(table) || table.length === 0) {
    // console.warn(`[getSizesInRange] table not found: ${tableName}`);
    return [];
  }

  let rangesRow = table.find((row) => row?.Component === component);

  if (!rangesRow) {
    rangesRow = table.find((row) => row?.Component === "Other");
  }

  if (!rangesRow) {
    // console.warn(
    //   `[getSizesInRange] Neither "${component}" nor "Other" found in ${tableName}`,
    // );
    return [];
  }

  let targetSizes = rangesRow?.Range;

  // Range might come as JSON string
  if (typeof targetSizes === "string") {
    try {
      const cleanedString = targetSizes.trim().replace(/;+$/, "");
      targetSizes = JSON.parse(cleanedString);
    } catch (err) {
      console.error(
        `[getSizesInRange] Failed to parse Range JSON for "${component}"`,
        err,
      );
      console.error(`[getSizesInRange] Raw Range:`, rangesRow?.Range);
      return [];
    }
  }

  if (!Array.isArray(targetSizes)) {
    // console.warn(
    //   `[getSizesInRange] Range for "${component}" is not an array:`,
    //   targetSizes,
    // );
    return [];
  }

  // "all sizes"
  if (!range || String(range).trim().toLowerCase() === "all sizes") {
    return [...targetSizes];
  }

  // parse like "1/2--4"
  const cleaned = String(range).trim();
  const [start, end] = cleaned.split("--").map((s) => s.trim());

  const startIndex = targetSizes.indexOf(start);
  const endIndex = targetSizes.indexOf(end);

  if (startIndex === -1 || endIndex === -1) {
    // console.warn(
    //   `[getSizesInRange] Invalid range "${range}". start="${start}" idx=${startIndex}, end="${end}" idx=${endIndex}`,
    // );
    return [];
  }

  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);

  return targetSizes.slice(minIndex, maxIndex + 1);
}

// -------------------------------
// Material code
// -------------------------------
export function generateMaterialCode(allTables, target) {
  let res = "";

  const codeComp = allTables["mc_components"]?.find(
    (item) => item?.Components === target?.Components,
  );
  res += codeComp?.Code || "";

  const codeSize = allTables["mc_structural_sizes_data"]?.find(
    (item) => normalizeSize(item?.Size) === normalizeSize(target?.Size),
  );
  res += codeSize?.Code || "";

  const codeType = allTables["mc_type"]?.find(
    (item) => item?.Type === target?.Types,
  );
  res += codeType?.Code || "";

  const codeMaterial = allTables["mc_materials"]?.find(
    (item) => item?.Material === target?.material,
  );
  res += codeMaterial?.Code || "";

  const codeRequirements = allTables["mc_requirements"]?.find(
    (item) => item?.Requirement === target?.Requirements,
  );
  res += codeRequirements?.Code || "";

  return res;
}

// -------------------------------
// Derived fields
// -------------------------------
export function updateDerivedFields(
  filteredSelectedSpecs,
  handleFieldUpdate,
  allTables,
  rowIndex,
  rowData,
) {
  const { Components, Size, Spec, OriginalSizeRange } = rowData;

  const baseMatch = (extra = {}) =>
    (filteredSelectedSpecs || []).find(
      (cur) =>
        cur?.Spec === Spec &&
        cur?.Components === Components &&
        cur?.Size === OriginalSizeRange &&
        (!rowData?.Material || cur?.Material === rowData?.Material) &&
        Object.entries(extra).every(([k, v]) => cur?.[k] === v),
    );

  const target = baseMatch();

  const material = target?.Material;

  handleFieldUpdate(rowIndex, "Material", material);

  if (Components && (OriginalSizeRange || Size) && material) {
    const desc = generateDescriptionFromDB(
      Components,
      Size,
      material,
      allTables,
      Spec,
      OriginalSizeRange,
    );
    handleFieldUpdate(rowIndex, "Material Description", desc);
  } else {
    handleFieldUpdate(rowIndex, "Material Description", "");
  }

  const Types = target?.["Est. (Ref)"];
  if (Types) handleFieldUpdate(rowIndex, "Types", Types);

  let Requirements;
  if (Spec && Components && OriginalSizeRange && material) {
    Requirements = (filteredSelectedSpecs || []).find(
      (cur) =>
        cur?.Spec === Spec &&
        cur?.Components === Components &&
        cur?.Size === OriginalSizeRange &&
        cur?.Material === material,
    )?.Requirements;

    if (Requirements) handleFieldUpdate(rowIndex, "Requirements", Requirements);
  }

  if (Components && Size && Requirements && material && Types) {
    const matCode = generateMaterialCode(allTables, {
      Components,
      Size,
      Requirements,
      material,
      Types,
    });
    handleFieldUpdate(rowIndex, "MaterialCode", matCode);
  }

  let i = rowIndex; // for logging context
  const structure_FamiliesTableName = Spec
    ? `structural_families_${Spec}`
    : null;

  // console.log(`[Row ${i}] Looking up estimation table for:`, {
  //   Components,
  //   Spec,
  //   structure_FamiliesTableName,
  // });

  if (!structure_FamiliesTableName) {
    console.warn(
      `[Row ${i}] Cannot generate piping families table name - missing Spec`,
    );
    return;
  }

  const structureFamiliesTable = allTables?.[structure_FamiliesTableName];

  if (!structureFamiliesTable || !structureFamiliesTable.length) {
    console.warn(
      `[Row ${i}] Piping families table not found: ${structure_FamiliesTableName}`,
    );
    // console.log(
    //   "Available tables:",
    //   Object.keys(allTables).filter((k) => k.includes("piping_families")),
    // );
    return;
  }

  const familyRow = structureFamiliesTable.find(
    (cur) => cur.Component === Components,
  );

  if (!familyRow) {
    console.warn(
      `[Row ${i}] Component "${Components}" not found in ${structure_FamiliesTableName}`,
    );
    // console.log(
    //   "Available components:",
    //   structureFamiliesTable.map((r) => r.Component),
    // );
    return;
  }

  const weightTableName = familyRow.Est_Table_Name;

  if (!weightTableName) {
    console.warn(
      `[Row ${i}] Est_Table_Name is empty for Component "${Components}"`,
    );
    // console.log("Family row data:", familyRow);
    return;
  }

  const targetTable = allTables?.[weightTableName] || [];

  if (!weightTableName) {
    // console.warn(
    //   `Cannot generate table name - missing Components or Spec at row ${i}`,
    // );
    return;
  }

  if (!targetTable) {
    // console.warn(`Table not found: ${weightTableName}`);
    return;
  }

  if (!targetTable.length) {
    // console.warn(`Table found but is empty: ${weightTableName}`);
    return;
  }

  const targetRow = targetTable.find(
    (row) => normalizeSize(row.Size) === normalizeSize(Size),
  );

  const unitWeight = targetRow?.Weight;
  if (unitWeight !== undefined) {
    handleFieldUpdate(rowIndex, "UnitWeight", unitWeight);
  }
}

export function resetSizeAndUpFields(handleFieldUpdate, rowIndex) {
  handleFieldUpdate(rowIndex, "Types", "");
  handleFieldUpdate(rowIndex, "Material", "");
  handleFieldUpdate(rowIndex, "Material Description", "");
  handleFieldUpdate(rowIndex, "MaterialCode", "");
  handleFieldUpdate(rowIndex, "Requirements", "");
  handleFieldUpdate(rowIndex, "Size", "");
  handleFieldUpdate(rowIndex, "OriginalSizeRange", "");
  handleFieldUpdate(rowIndex, "Family Table", "");
  handleFieldUpdate(rowIndex, "FamilyTableTarget", "");
  handleFieldUpdate(rowIndex, "UnitWeight", "");
}

// -------------------------------
// Family table options
// -------------------------------
export function getFamilyTableOptions(allTables, component, spec) {
  if (!spec || !component || !allTables) return [];

  const targetTableKey = Object.keys(allTables).find(
    (key) => key.includes("structural_families") && key.includes(spec),
  );

  if (!targetTableKey) return [];

  const targetTable = allTables[targetTableKey];

  return (targetTable || [])
    .filter((cur) => cur?.Component === component)
    .map((cur) => ({
      Family_Name: cur?.Family_Name,
      Family_Table: cur?.Table_Name,
    }))
    .filter(Boolean);
}

// -------------------------------
// Description from DB
// -------------------------------
export function generateDescriptionFromDB(
  Components,
  Size,
  Material,
  allTables,
  Spec,
  OriginalSizeRange,
) {
  const tableName = Spec ? `structural_families_${Spec}` : null;
  if (!tableName) return "";

  const table = allTables?.[tableName];
  if (!Array.isArray(table) || table.length === 0) return "";

  const searchSize = (OriginalSizeRange || Size || "").trim();

  const familyRow = table.find((cur) => {
    const dbComponent = String(cur?.Component || "")
      .trim()
      .toLowerCase();
    const componentMatch =
      dbComponent === String(Components || "").toLowerCase();
    if (!componentMatch) return false;

    if (cur?.Size !== undefined && cur?.Size !== null) {
      const dbSize = String(cur?.Size || "").trim();
      if (searchSize && dbSize !== searchSize) return false;
    }
    return true;
  });

  if (!familyRow) return "";

  const dt = String(familyRow?.Description_String ?? "").trim();
  if (!dt || dt === "-") return "DB Error";

  let description = dt;
  description = description.replace(/\{component\}/gi, Components || "");

  // If Size like "A x B" numeric (lowercase x)
  let actualSize = Size || "";
  if (
    Size &&
    Size.includes("x") &&
    (() => {
      const parts = Size.split("x").map((s) => s.trim());
      return parts.length === 2 && parts.every((p) => /^[\d\s\/\.]+$/.test(p));
    })()
  ) {
    const parts = Size.split("x").map((s) => s.trim());
    actualSize = parts[1] || "";
  }

  description = description.replace(/\{size\}/gi, actualSize);
  description = description.replace(/\{material\}/gi, Material || "");

  return description;
}
